import { toastErro, toastSucesso } from "components/Toast";
import { useEffect, useState } from "react";
import { Alert, Button, Card, Form, InputGroup, Spinner } from "react-bootstrap";
import { FaEye, FaEyeSlash, FaShieldAlt, FaUserAlt } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import logoDoc from "../../../../_assets/images/logo_lw_vermelho.png";
import { IDadosLinkDetran, IDefinirSenhaDetranForm } from "../../@types";
import { useDefinirSenhaDetran } from "../../hooks/useDefinirSenhaDetran";

const COR_MARCA = "#1A1A1A";
const COR_TEXTO_SECUNDARIO = "#6c757d";
const COR_GRUPO_CAMPOS = "#f5f5f5";

export function DefinirSenhaDetran() {
  const { token = "" } = useParams();
  const { validarToken, definirSenha, loading } = useDefinirSenhaDetran();

  const [dadosLink, setDadosLink] = useState<IDadosLinkDetran | null>(null);
  const [tokenInvalido, setTokenInvalido] = useState<boolean>(false);
  const [validando, setValidando] = useState<boolean>(true);
  const [sucesso, setSucesso] = useState<boolean>(false);
  const [mostrarSenha, setMostrarSenha] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IDefinirSenhaDetranForm>();

  const CODIGOS_DETRAN_VAZIOS = ["000000-0", "0000000", "00000000", "000000"];
  const codDespachante = dadosLink?.codDespachante?.trim() ?? "";
  const precisaCodigoDetran = !codDespachante || CODIGOS_DETRAN_VAZIOS.includes(codDespachante);

  useEffect(() => {
    async function run() {
      const { hasErro, data } = await validarToken(token);

      if (hasErro) {
        setTokenInvalido(true);
      } else {
        setDadosLink(data);
        if (data?.usuario) {
          setValue("usuario", data.usuario);
        }
      }

      setValidando(false);
    }

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onSubmit(form: IDefinirSenhaDetranForm) {
    const { hasErro, data } = await definirSenha(token, form);

    if (hasErro) {
      const mensagem = Array.isArray(data?.message)
        ? data.message.join(", ")
        : data?.message || "Não foi possível salvar as credenciais";
      toastErro(mensagem);
      return;
    }

    toastSucesso("Credenciais salvas com sucesso");
    setSucesso(true);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        padding: "32px 16px",
      }}
    >
      <img
        src={logoDoc}
        alt="LW DOC"
        style={{ height: "40px", width: "117.04px", marginBottom: "24px" }}
      />

      <h1
        style={{
          fontSize: "24px",
          fontWeight: 500,
          color: COR_MARCA,
          margin: 0,
          textAlign: "center",
        }}
      >
        Definir senha Detran
      </h1>
      <p
        style={{
          fontSize: "14px",
          color: COR_TEXTO_SECUNDARIO,
          margin: "8px 0 24px",
          textAlign: "center",
        }}
      >
        Informe seu usuário e senha para realizar a integração.
      </p>

      <Card
        style={{
          width: "100%",
          maxWidth: "400px",
          border: "1px solid #e6e6e6",
          borderRadius: "8px",
          boxShadow: "0 2px 12px rgba(0,0,0,.06)",
        }}
      >
        <Card.Body style={{ padding: "24px" }}>
          {validando && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spinner animation="border" role="status" />
            </div>
          )}

          {!validando && tokenInvalido && (
            <p style={{ color: "#dc3545", margin: 0 }}>
              Este link é inválido ou expirou. Solicite um novo link para definir a senha.
            </p>
          )}

          {!validando && !tokenInvalido && sucesso && (
            <p style={{ color: "#28a745", margin: 0 }}>
              Credenciais salvas com sucesso. Você já pode fechar esta página.
            </p>
          )}

          {!validando && !tokenInvalido && !sucesso && dadosLink && (
            <>
              <p style={{ color: COR_TEXTO_SECUNDARIO, marginBottom: "16px", fontSize: "0.9rem" }}>
                Despachante: <strong style={{ color: COR_MARCA }}>{dadosLink.despachante}</strong>
                {!precisaCodigoDetran && (
                  <>
                    <br />
                    Código Detran: <strong style={{ color: COR_MARCA }}>{dadosLink.codDespachante}</strong>
                  </>
                )}
              </p>

              <Form onSubmit={handleSubmit(onSubmit)}>
                <div
                  style={{
                    background: COR_GRUPO_CAMPOS,
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  {precisaCodigoDetran && (
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: "0.8rem", color: COR_TEXTO_SECUNDARIO }}>
                        Código Detran
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Informe o código Detran"
                        isInvalid={!!errors.codDetran}
                        {...register("codDetran", { required: "Código Detran é obrigatório" })}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.codDetran?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "0.8rem", color: COR_TEXTO_SECUNDARIO }}>
                      Usuário
                    </Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control
                        type="text"
                        placeholder="Informe o usuário"
                        isInvalid={!!errors.usuario}
                        {...register("usuario", { required: "Usuário é obrigatório" })}
                      />
                      <InputGroup.Text style={{ background: "#ffffff" }}>
                        <FaUserAlt color={COR_TEXTO_SECUNDARIO} />
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.usuario?.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label style={{ fontSize: "0.8rem", color: COR_TEXTO_SECUNDARIO }}>
                      Senha
                    </Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control
                        type={mostrarSenha ? "text" : "password"}
                        placeholder="Informe a senha"
                        isInvalid={!!errors.senha}
                        {...register("senha", { required: "Senha é obrigatória" })}
                      />
                      <InputGroup.Text
                        role="button"
                        onClick={() => setMostrarSenha((prev) => !prev)}
                        style={{ cursor: "pointer", background: "#ffffff" }}
                        aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                      >
                        {mostrarSenha ? (
                          <FaEyeSlash color={COR_TEXTO_SECUNDARIO} />
                        ) : (
                          <FaEye color={COR_TEXTO_SECUNDARIO} />
                        )}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.senha?.message}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </div>

                <Alert
                  variant="info"
                  className="d-flex align-items-start mt-3 mb-0"
                  style={{ border: "none" }}
                >
                  <FaShieldAlt className="mt-1 me-2 flex-shrink-0" />
                  <span style={{ fontSize: "0.8rem" }}>
                    As credenciais informadas serão armazenadas de forma criptografada,
                    garantindo a confidencialidade da senha. Essas informações serão utilizadas
                    exclusivamente para autenticação da integração e não serão exibidas em texto
                    legível.
                  </span>
                </Alert>

                <Button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    marginTop: "24px",
                    background: COR_MARCA,
                    border: "none",
                    borderRadius: "6px",
                    padding: "10px",
                    fontWeight: 600,
                  }}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : "Salvar"}
                </Button>
              </Form>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
